import { lazy, Suspense, type RefObject } from 'react';
import { useInView } from '../hooks/useInView';
import { isWebGlAvailable } from '../lib/webgl';

const ClinicStethoscopeCanvas = lazy(() =>
  import('./ClinicStethoscopeCanvas').then((module) => ({
    default: module.ClinicStethoscopeCanvas,
  })),
);

/**
 * Owns every decision about whether a 3D stage should exist: it defers the
 * three.js chunk until the stage is approached, keeps it off clients without
 * WebGL, and parks the render loop once the stage leaves the screen.
 */
export function ClinicCanvasStage({
  scrollProgress,
  progressRef,
  compact = false,
  hero = false,
  pointerEnabled = true,
  showLoader = false,
}: Props) {
  const { ref, inView, hasBeenInView } = useInView<HTMLDivElement>('400px');
  const canRender = hasBeenInView && isWebGlAvailable();

  return (
    <div ref={ref} className="absolute inset-0" aria-hidden="true">
      {canRender ? (
        <Suspense fallback={<StageFallback showLoader={showLoader} />}>
          <ClinicStethoscopeCanvas
            scrollProgress={scrollProgress}
            progressRef={progressRef}
            active={inView}
            compact={compact}
            hero={hero}
            pointerEnabled={pointerEnabled}
          />
        </Suspense>
      ) : (
        <StageFallback showLoader={showLoader} />
      )}
    </div>
  );
}

function StageFallback({ showLoader }: StageFallbackProps) {
  if (!showLoader) {
    return <div className="absolute inset-0 bg-stage" />;
  }

  return (
    <div className="absolute inset-0 flex items-center justify-center bg-stage">
      <div className="h-10 w-10 animate-pulse rounded-full border-2 border-[rgba(61,168,156,0.35)] border-t-primary-400" />
    </div>
  );
}

interface StageFallbackProps {
  showLoader: boolean;
}

interface Props {
  scrollProgress?: number;
  progressRef?: RefObject<number>;
  compact?: boolean;
  hero?: boolean;
  pointerEnabled?: boolean;
  showLoader?: boolean;
}
